import { useAppContext } from '@/context/state';
import { RootState } from '@/store/store';
import { FacetValue } from '@/lib/models/types/product';
import {
  ICmsCategoriesFilters,
  ICmsTagsGroup,
  ServerSideCatalogQuery,
} from '@/types/collections';
import {
  getCatalogQuery,
  getCollectionUrlInfo,
  collectionSortOptions,
} from '@/utils/Catalog';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Collection from '@/components/organisms/Collection';
import CollectionFilters from '../CollectionFilters';
import styled from 'styled-components';
import { useMediaQuery } from '@material-ui/core';
import { ChipsList, SortCollection } from '@/components/molecules';
import theme from '@/theme/theme';
import { ProductSearch } from '@/lib/models/types/product';

interface ICollectionHandler {
  cmsCollectionTags: ICmsCategoriesFilters;
  serverProducts: ProductSearch;
  serverFilters: ICmsTagsGroup | null;
  serverCategories: FacetValue[];
  serverSideCatalogQuery: ServerSideCatalogQuery;
}
const FiltersWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
  margin-bottom: 16px;
  ${props => props.theme.breakpoints.up('md')} {
    margin-bottom: 0;
    width: 25%;
    max-width: 320px;
  }
`;

const CollectionWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  ${props => props.theme.breakpoints.up('md')} {
    width: 75%;
  }
  ${props => props.theme.breakpoints.up('lg')} {
    width: 80%;
  }
`;
const ChipsSortWrapper = styled.div`
  /* padding: 0 16px; */
  display: flex;
  position: relative;
  width: 100%;
  min-height: 32px;
  flex-direction: row-reverse;
  align-items: center;
  margin-bottom: 16px;
`;

const Container = styled.div`
  margin-bottom: 56px;
  position: relative;

  ${props => props.theme.breakpoints.up('md')} {
    display: flex;
    gap: 56px;
    padding: 0;
  }
  ${props => props.theme.breakpoints.up('lg')} {
    gap: 80px;
  }
`;
const CollectionHandler: React.FC<ICollectionHandler> = ({
  cmsCollectionTags,
  serverProducts,
  serverFilters,
  serverCategories,
  serverSideCatalogQuery,
}) => {
  const router = useRouter();
  const context = useAppContext();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const settings = context?.settings;
  const { selectedSort } = getCollectionUrlInfo(
    router.query,
    router.asPath,
    settings || undefined
  );

  //state
  const [query, setCatalogQuery] = useState<{
    productsQuery: string;
    filtersQuery: string;
  } | null>(null);

  //from redux
  const currency = useSelector(
    (state: RootState) => state.currencyReducer.currency
  );
  const loadingCustomer = useSelector(
    (state: RootState) => state.userReducer.loadingCustomer
  );
  const segmentPrice = useSelector(
    (state: RootState) => state.userReducer?.customer?.segmentPrice?.value
  );

  useEffect(() => {
    if (settings && currency && !loadingCustomer) {
      if (
        currency.isoCode === serverSideCatalogQuery.currency &&
        router.asPath === serverSideCatalogQuery.path
      ) {
        setCatalogQuery(null);
        return;
      }

      const productsQuery = getCatalogQuery(
        router.query,
        router.asPath,
        settings,
        currency.isoCode,
        cmsCollectionTags,
        'products',
        segmentPrice
      );
      const filtersQuery = getCatalogQuery(
        router.query,
        router.asPath,
        settings,
        currency.isoCode,
        cmsCollectionTags,
        'filters',
        segmentPrice
      );

      setCatalogQuery({
        productsQuery: productsQuery,
        filtersQuery: filtersQuery,
      });
    }
  }, [router.asPath, currency, segmentPrice, settings, loadingCustomer]);

  return (
    <Container id="filtersContainer">
      <FiltersWrapper>
        <div id="stickyFilters">
          {currency && (
            <CollectionFilters
              filtersQuery={query?.filtersQuery || null}
              cmsCollectionTags={cmsCollectionTags}
              serverFilters={serverFilters}
              serverCategories={serverCategories}
              currency={currency}
            />
          )}
        </div>
      </FiltersWrapper>
      <CollectionWrapper>
        {!isMobile && (
          <ChipsSortWrapper>
            <SortCollection
              sortSelected={selectedSort}
              collectionSortOptions={collectionSortOptions}
            />
            {currency && (
              <ChipsList
                collectionTags={cmsCollectionTags}
                currency={currency}
              />
            )}
          </ChipsSortWrapper>
        )}
        <Collection
          productsQuery={query?.productsQuery || null}
          showItemsNumber={true}
          serverQueryResult={serverProducts}
        />
      </CollectionWrapper>
    </Container>
  );
};

export default CollectionHandler;
